import { createContext, useContext, useEffect, useId, useState, type ReactNode, type LabelHTMLAttributes } from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { CircleAlert } from 'lucide-react'
import { cn } from './cn'

interface FieldCtx {
  id: string; helpId: string; errorId: string; invalid: boolean; required: boolean; disabled: boolean; describedBy?: string
  /** A control reports text it can't turn into a value ("Enter at most $1,000.00"); the Field shows it. */
  setDraftError: (message: string | null) => void
}
const FieldContext = createContext<FieldCtx | null>(null)

/**
 * Controls (Input, Textarea, Select, Combobox, NumberInput, MoneyInput, TagInput…) call this to
 * pick up id / aria-describedby / aria-invalid from the surrounding <Field>. Returns null outside one.
 */
export function useFieldControl() { return useContext(FieldContext) }

/**
 * For controls that keep a text draft (MoneyInput, NumberInput, ColorPicker): show `message` as the
 * surrounding Field's error while the draft is invalid (a form/server error still wins).
 */
export function useDraftError(message: string | null) {
  const set = useContext(FieldContext)?.setDraftError
  useEffect(() => { set?.(message) }, [message, set])
  useEffect(() => () => set?.(null), [set])
}

export function Label({ className, required, children, ...props }: LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }) {
  return (
    <LabelPrimitive.Root className={cn('inline-flex items-center gap-1 text-base font-medium text-text', className)} {...props}>
      {children}
      {required && <span aria-hidden className="text-danger-strong">*</span>}
    </LabelPrimitive.Root>
  )
}

export interface FieldProps {
  label?: ReactNode
  /** Help text under the control (hidden while an error shows). */
  help?: ReactNode
  /** Error message (e.g. form.errors.title or a server `fields` message). */
  error?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Extra content on the label row, right-aligned (e.g. a character count or AiWriteButton). */
  labelAside?: ReactNode
  /** Explicit id for the control; generated otherwise. */
  id?: string
  /** Visually hide the label (still read by screen readers). */
  hideLabel?: boolean
  /** Horizontal layout: label left, control right (settings rows). */
  inline?: boolean
  className?: string
  children: ReactNode
}

/**
 * Label + control + help/error, wired for accessibility automatically:
 * `<Field label="Title" required error={form.errors.title}><Input {...form.bind('title')} /></Field>`
 */
export function Field({ label, help, error, required = false, disabled = false, labelAside, id, hideLabel, inline, className, children }: FieldProps) {
  const auto = useId()
  const cid = id ?? `f${auto.replace(/:/g, '')}`
  const helpId = `${cid}-help`, errorId = `${cid}-error`
  const [draftError, setDraftError] = useState<string | null>(null)
  const shown = error || draftError
  const invalid = !!shown
  const describedBy = [invalid ? errorId : null, help && !invalid ? helpId : null].filter(Boolean).join(' ') || undefined
  return (
    <FieldContext.Provider value={{ id: cid, helpId, errorId, invalid, required, disabled, describedBy, setDraftError }}>
      <div className={cn(inline ? 'grid gap-x-6 gap-y-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:items-start' : 'flex flex-col gap-2', disabled && 'opacity-60', className)}>
        {(label || labelAside) && (
          <div className={cn('flex min-h-5 items-center justify-between gap-2', hideLabel && 'sr-only', inline && 'sm:min-h-8')}>
            {label && <Label htmlFor={cid} required={required}>{label}</Label>}
            {labelAside && <div className="text-sm text-text-subtle">{labelAside}</div>}
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-2">
          {children}
          {invalid ? (
            <p id={errorId} role="alert" className="flex items-start gap-2 text-sm text-danger-strong">
              <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
              <span>{shown}</span>
            </p>
          ) : help ? (
            <p id={helpId} className="text-sm text-text-subtle">{help}</p>
          ) : null}
        </div>
      </div>
    </FieldContext.Provider>
  )
}

/** Group of related fields with an optional legend (e.g. "Pricing"). Lays children out with even spacing. */
export function Fieldset({ legend, description, children, className }: { legend?: ReactNode; description?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <fieldset className={cn('flex flex-col gap-4', className)}>
      {legend && (
        <div className="flex flex-col gap-1">
          <legend className="text-base font-semibold text-text">{legend}</legend>
          {description && <p className="text-sm text-text-muted">{description}</p>}
        </div>
      )}
      {children}
    </fieldset>
  )
}

/** Responsive grid for side-by-side fields: `<FieldRow><Field…/><Field…/></FieldRow>` (stacks below 640 px). */
export function FieldRow({ children, cols = 2, className }: { children: ReactNode; cols?: 2 | 3 | 4; className?: string }) {
  return <div className={cn('grid gap-4', cols === 2 && 'sm:grid-cols-2', cols === 3 && 'sm:grid-cols-3', cols === 4 && 'sm:grid-cols-2 lg:grid-cols-4', className)}>{children}</div>
}
